import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { graphGetJson } from "../../email/services/graph";
import type { CalEvent } from "../types";
import { withoutPrefixes } from "./agendaDraft";

interface Recipient {
  readonly emailAddress?: { readonly name?: string | null; readonly address?: string | null } | null;
}

/** A meeting's email thread: who sent its latest email ("Your" if you did), and that email, to open it. */
export interface MeetingThread {
  readonly sender: string;
  readonly messageId: string;
  /** Outlook on the web's link to the email, for when the Outlook app can't open it. */
  readonly webLink: string;
}

interface ThreadSearchResponse {
  readonly value?: readonly {
    readonly "@odata.type"?: string;
    /** Always returned, whatever is selected. */
    readonly id?: string;
    readonly subject?: string | null;
    readonly from?: Recipient | null;
    readonly toRecipients?: readonly Recipient[] | null;
    readonly ccRecipients?: readonly Recipient[] | null;
    readonly receivedDateTime?: string | null;
  }[];
}

const addressOf = (r: Recipient | null | undefined) => (r?.emailAddress?.address ?? "").trim().toLowerCase();

/** Outlook on the web's link to a message, in the shape of Graph's own webLink (the search doesn't ask for it). */
const owaReadLink = (id: string) => `https://outlook.office365.com/owa/?ItemID=${encodeURIComponent(id)}&exvsurl=1&viewmodel=ReadMessageItem`;

/**
 * The latest email about `event`: same subject, with someone else on the meeting in it.
 * Invites and replies to them don't count; null when there's none.
 */
export async function findMeetingThread(
  token: string,
  refresh: RefreshTokenFn | undefined,
  event: Pick<CalEvent, "subject" | "organizerEmail" | "attendees">,
  selfEmails: ReadonlySet<string>,
): Promise<MeetingThread | null> {
  const people = [event.organizerEmail, ...event.attendees.filter((a) => a.type !== "resource").map((a) => a.email)];
  const others = new Set(people.map((e) => e.trim().toLowerCase()).filter((e) => e && !selfEmails.has(e)));
  const wanted = withoutPrefixes(event.subject).toLowerCase();
  const query = wanted.replace(/["\\]/g, " ").trim();
  if (others.size === 0 || !query) return null;
  const search = encodeURIComponent(`"subject:${query}"`);
  const result = await graphGetJson<ThreadSearchResponse>(
    `/me/messages?$search=${search}&$top=10&$select=subject,from,toRecipients,ccRecipients,receivedDateTime`,
    token,
    refresh,
  );
  const latest = (result.value ?? [])
    .filter((m) => !m["@odata.type"]?.includes("eventMessage") && withoutPrefixes(m.subject ?? "").toLowerCase() === wanted)
    .filter((m) => [m.from, ...(m.toRecipients ?? []), ...(m.ccRecipients ?? [])].some((r) => others.has(addressOf(r))))
    .sort((a, b) => Date.parse(b.receivedDateTime ?? "") - Date.parse(a.receivedDateTime ?? ""))[0];
  if (!latest?.id) return null;
  const from = addressOf(latest.from);
  const sender = selfEmails.has(from) ? "Your" : (latest.from?.emailAddress?.name ?? "").trim() || from;
  return { sender, messageId: latest.id, webLink: owaReadLink(latest.id) };
}
